import React, { useState, useEffect, useCallback } from 'react';
import { DataTable } from '@/components/DataTable';
import { staffService } from '@/services/staff';
import { usersService } from '@/services/users';
import { getApiErrorMessage } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Plus } from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const STATUS_OPTIONS = ['Active', 'Inactive', 'On Leave'];

function roleDisplayName(roleId, rolesList) {
  if (!roleId) return '—';
  const r = rolesList.find((x) => x.id === roleId);
  return r ? r.name : roleId;
}

export default function HRPage() {
  const [staff, setStaff] = useState([]);
  const [roles, setRoles] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name: '',
    role: '',
    department: '',
    email: '',
    status: 'Active',
  });

  const loadStaff = useCallback(async () => {
    const r = await staffService.getAll();
    setStaff(r.data);
  }, []);

  const loadRoles = useCallback(async () => {
    const { data } = await usersService.getRoles();
    setRoles(data);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await Promise.all([loadStaff(), loadRoles()]);
      } catch (error) {
        toast.error(getApiErrorMessage(error));
      }
    })();
  }, [loadStaff, loadRoles]);

  const openAdd = () => {
    setEditingId(null);
    setForm({
      name: '',
      role: '',
      department: '',
      email: '',
      status: 'Active',
    });
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      name: row.name || '',
      role: row.role || '',
      department: row.department || '',
      email: row.email || '',
      status: row.status || 'Active',
    });
    setFormOpen(true);
  };

  const saveStaff = async () => {
    try {
      const payload = {
        name: form.name,
        role: form.role,
        department: form.department,
        email: form.email || undefined,
        status: form.status,
      };
      if (editingId) {
        await staffService.update(editingId, payload);
        toast.success('Staff member updated');
      } else {
        await staffService.create(payload);
        toast.success('Staff member added');
      }
      setFormOpen(false);
      await loadStaff();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const columns = [
    {
      key: 'name',
      header: 'Staff Member',
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center gap-3">
          <Avatar className="h-10 w-10">
            <AvatarImage src={row.avatar} alt={value} />
            <AvatarFallback>{value?.charAt(0)}</AvatarFallback>
          </Avatar>
          <span className="font-medium">{value}</span>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      sortable: true,
      render: (value) => (
        <span className="text-sm">{roleDisplayName(value, roles)}</span>
      ),
    },
    { key: 'department', header: 'Department', sortable: true },
    {
      key: 'status',
      header: 'Status',
      render: (value) => (
        <Badge variant={value === 'Active' ? 'default' : 'secondary'}>
          {value}
        </Badge>
      ),
    },
  ];

  return (
    <div className="space-y-6" data-testid="hr-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-light tracking-tighter mb-2">HR & Staff</h1>
          <p className="text-sm md:text-base leading-relaxed text-muted-foreground">
            Manage hospital staff. Assign a role from the same list used for system access (configured
            under Role Management).
          </p>
        </div>
        <Dialog
          open={formOpen}
          onOpenChange={(open) => {
            setFormOpen(open);
            if (!open) setEditingId(null);
          }}
        >
          <Button data-testid="add-staff-button" onClick={openAdd}>
            <Plus className="mr-2 h-4 w-4" weight="bold" />
            Add Staff
          </Button>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingId ? 'Edit staff member' : 'Add staff member'}</DialogTitle>
              <DialogDescription>
                {editingId
                  ? 'Update details and role for this directory entry.'
                  : 'Add someone to the staff directory and assign their role.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="staff-name">Full name</Label>
                <Input
                  id="staff-name"
                  placeholder="Dr. John Smith"
                  value={form.name}
                  onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select
                  value={form.role}
                  onValueChange={(v) => setForm((s) => ({ ...s, role: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {form.role && !roles.some((r) => r.id === form.role) ? (
                      <SelectItem value={form.role}>
                        {form.role} (custom)
                      </SelectItem>
                    ) : null}
                    {roles.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="staff-dept">Department</Label>
                <Input
                  id="staff-dept"
                  placeholder="Cardiology"
                  value={form.department}
                  onChange={(e) => setForm((s) => ({ ...s, department: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="staff-email">Email</Label>
                <Input
                  id="staff-email"
                  type="email"
                  placeholder="john@hospital.com"
                  value={form.email}
                  onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={form.status}
                  onValueChange={(v) => setForm((s) => ({ ...s, status: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button className="w-full" type="button" onClick={saveStaff}>
                {editingId ? 'Save changes' : 'Add staff member'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <DataTable
        data={staff}
        columns={columns}
        searchPlaceholder="Search staff..."
        onRowClick={openEdit}
      />
    </div>
  );
}
